import React, {useCallback, useContext, useEffect, useMemo, useReducer} from 'react';
import PropTypes from 'prop-types';
import {useApp} from '@app';
import {UserApplication} from '@application/User';
import {eventManager} from '@application/Event';
import {AuthApplication} from '@application/Auth';
import {useSettings} from '@components/_context';
import {useLoadUserOverallInfoInternetAware} from './_hooks/useLoadUserOverallInfoInternetAware';
import {UserEvent} from '@domain/model/Event';
import {COUNTRY} from '@domain/model/Site/Locale';
import {OrderStatus} from '@domain/model/Order';
import {firstAvailableCheckoutByCustomerId} from '@modules/checkout/bootstrap';
import {useMendel} from '@lookiero/mendel-web-client';
import {PaymentMethods, usePaymentInstrument} from '@lookiero/payments-front';
import {DEFAULT_SEGMENT} from '@domain/model/User/Segment';
import {sendLogMessage} from '@infra/domain/model/ServerLog/ServerLogRepository';
import {sendException} from '../../../../observability';

const INITIAL_STATE = {
  WIPOrder: false,
  card: {},
  country: null,
  dateCreated: '',
  email: '',
  hasAnyOrders: false,
  id: null,
  loading: true,
  loadUserOverallInfo: async () => {},
  overallInfoError: false,
  orders: [],
  checkout: /** @type {any} */ (undefined),
  nextBoxDate: '',
  estimatedCarrierDeliveryAt: '',
  isInterimOrder: false,
  userNextBoxDate: '',
  personalCode: '',
  billingAddress: {},
  shippingAddress: {},
  subscription: {
    id: undefined,
    modality: undefined,
    lkDay: undefined,
  },
  username: '',
  executeSkipMonth: () => null,
  resetState: () => null,
  segment: DEFAULT_SEGMENT,
  keepLoadingUserOverallInfo: () => {},
  migrated: false,
};

const POOLING_MAX_RETRIES = 10;

const UserOverallInfoContext = React.createContext(INITIAL_STATE);

const ACTIONS = {
  EXECUTE_ACTION_CHANGE_MODALITY_TO_SUBSCRIBER: 'execute-action-change-modality-to-subscriber',
  EXECUTE_ACTION_SKIP_MONTH: 'execute-action-skip-month',
  EXECUTE_ACTION_UPDATE_ORDER: 'execute-action-update-order',
  UPDATE_NEXT_BOX_DATE: 'update-next-box-date',
  UPDATE_ESTIMATED_CARRIER_DELIVERY_AT: 'update-estimated-carrier-delivery-at',
  LOAD_USER_OVERALL_INFO: 'load-user-overall-info',
  LOAD_USER_OVERALL_INFO_ERROR: 'load-user-overall-info-error',
  RESET_ACTION_EXECUTED: 'reset-action-executed',
  RESET_STATE: 'reset-state',
  SET_LOADING: 'set_loading',
  UPDATE_CARD: 'update-card',
  UPDATE_COUNTRY: 'update-country',
  LOAD_USER_OVERALL_INFO_WIP: 'load-user-overall-info-wip',
};

// TODO Albareto esto da mucho asco, si tuviera el culo menos apretado lo arreglaba pero hoy se queda así
const updateOrder = (state, {id, ...rest}) => {
  for (let i = 0, l = state.orders.length; i < l; i++) {
    const services = state.orders[i].services;
    for (let j = 0, ll = services.length; j < ll; j++) {
      if (services[j].id === id) {
        services[j] = {...services[j], ...rest};
      }
    }
  }
  return state;
};

const reducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.LOAD_USER_OVERALL_INFO:
      return {...state, ...action.payload, overallInfoError: false};
    case ACTIONS.LOAD_USER_OVERALL_INFO_ERROR:
      return {...state, loading: false, overallInfoError: true};
    case ACTIONS.LOAD_USER_OVERALL_INFO_WIP:
      return {...state, WIPOrder: true};
    case ACTIONS.UPDATE_CARD:
      return {...state, card: {...state.card, ...action.payload}};
    case ACTIONS.UPDATE_COUNTRY:
      return state.country === action.payload ? state : {...state, country: action.payload};
    case ACTIONS.EXECUTE_ACTION_UPDATE_ORDER:
      return {...updateOrder(state, action.payload)};
    case ACTIONS.UPDATE_NEXT_BOX_DATE:
      return {...state, nextBoxDate: action.payload};
    case ACTIONS.UPDATE_ESTIMATED_CARRIER_DELIVERY_AT:
      return {...state, estimatedCarrierDeliveryAt: action.payload};
    case ACTIONS.EXECUTE_ACTION_SKIP_MONTH:
    case ACTIONS.EXECUTE_ACTION_CHANGE_MODALITY_TO_SUBSCRIBER:
      return {...state, WIPOrder: true};
    case ACTIONS.RESET_ACTION_EXECUTED:
      return {...state, WIPOrder: false, ...action.payload};
    case ACTIONS.SET_LOADING:
      return {...state, loading: action.payload, overallInfoError: action.payload ? false : state.overallInfoError};
    case ACTIONS.RESET_STATE:
      return INITIAL_STATE;
    default:
      return state;
  }
};

const removeNullablePropsFromObject = item =>
  Object.keys(item).reduce((acc, key) => {
    if (item[key] !== undefined && item[key] !== null) {
      acc[key] = item[key];
    }
    return acc;
  }, {});

// TODO repeated definition in some other place. Searh for it.
const ORDER_IN_PROGRESS_STATUSES = [
  OrderStatus.PEDIDO_REALIZADO,
  OrderStatus.EN_MARCHA,
  OrderStatus.BORRADOR,
  OrderStatus.EN_PREPARACION,
  OrderStatus.ENVIADO,
  null,
];

const deliveredByCheckoutOrder = (orders, checkout) =>
  checkout?.boxId != null ? orders.find(({services: {0: order}}) => order.boxId === checkout.boxId) : undefined;

const inProgressByStatusOrder = orders =>
  orders.find(({services: {0: order}}) => ORDER_IN_PROGRESS_STATUSES.includes(order.status));

// The delivered box no longer carries a legacy status (ENTREGADO removed): it is identified by joining on
// `boxId` against the customer's first available checkout (UUID === UUID) and TAKES PRECEDENCE over the status match.
const findInProgressOrder = (orders, checkout) =>
  deliveredByCheckoutOrder(orders, checkout) ?? inProgressByStatusOrder(orders);

const findWIPOrder = ({orders = []}, checkout) => {
  if (!orders.length) {
    return true;
  }

  const {
    services: {0: {shippingAddress, nextBoxDate} = {}},
  } = findInProgressOrder(orders, checkout) || {services: []};

  return !shippingAddress || !nextBoxDate;
};

const getNextBoxDate = (orders = [], checkout) => {
  const delivered = deliveredByCheckoutOrder(orders, checkout);
  return (
    (delivered?.services?.[0]?.nextBoxDate ?? null) ||
    orders.reduce((date, {services: {0: order}}) => {
      const orderNextBoxDate = (ORDER_IN_PROGRESS_STATUSES.includes(order.status) && order.nextBoxDate) || null;
      return date || orderNextBoxDate;
    }, null)
  );
};

const getEstimatedCarrierDeliveryAt = (orders = [], checkout) => {
  const delivered = deliveredByCheckoutOrder(orders, checkout);
  return (
    (delivered?.services?.[0]?.estimatedCarrierDeliveryAt ?? null) ||
    orders.reduce((date, {services: {0: order}}) => {
      const orderEstimatedCarrierDeliveryAt =
        (ORDER_IN_PROGRESS_STATUSES.includes(order.status) && order.estimatedCarrierDeliveryAt) || null;
      return date || orderEstimatedCarrierDeliveryAt;
    }, null)
  );
};

const getCurrentOrderIsInterim = (orders = [], checkout) =>
  Boolean(findInProgressOrder(orders, checkout)?.services?.[0]?.isInterimOrder);

const getCurrentOrderUserNextBoxDate = (orders = [], checkout) =>
  findInProgressOrder(orders, checkout)?.services?.[0]?.userNextBoxDate || '';

const UserOverallInfoProvider = ({children}) => {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  const {site, updateSettingsByCountry} = useSettings();
  const paymentInstrument = usePaymentInstrument();
  const {setAuthorization} = useApp();
  const mendelService = useMendel();

  const loadUserOverallInfo = useCallback(
    async (loading = true) => {
      if (loading && !state.loading) {
        dispatch({type: ACTIONS.SET_LOADING, payload: loading});
      }
      try {
        const updatedUserInfo = await UserApplication.getUserOverallInfo();
        // A checkout outage must NOT block the core overall-info load: degrade a rejection to undefined.
        const checkout = updatedUserInfo?.id
          ? await firstAvailableCheckoutByCustomerId({customerId: updatedUserInfo.id}).catch(() => undefined)
          : undefined;

        const sanitizedUserInfo = removeNullablePropsFromObject(updatedUserInfo);
        const userInfoWithNextBoxDate = {
          ...sanitizedUserInfo,
          checkout,
          nextBoxDate: getNextBoxDate(sanitizedUserInfo.orders, checkout),
          estimatedCarrierDeliveryAt: getEstimatedCarrierDeliveryAt(sanitizedUserInfo.orders, checkout),
          isInterimOrder: getCurrentOrderIsInterim(sanitizedUserInfo.orders, checkout),
          userNextBoxDate: getCurrentOrderUserNextBoxDate(sanitizedUserInfo.orders, checkout),
        };

        eventManager.emit(UserEvent.USER_LOADED, userInfoWithNextBoxDate);
        dispatch({type: ACTIONS.LOAD_USER_OVERALL_INFO, payload: {...userInfoWithNextBoxDate, loading: false}});
      } catch (error) {
        sendException(error);
        dispatch({type: ACTIONS.LOAD_USER_OVERALL_INFO_ERROR});
      }
    },
    [state.loading],
  );
  useLoadUserOverallInfoInternetAware({loadUserOverallInfo, loading: state.loading});

  const getUserOverallInfoPooling = async ({
    dispatch,
    retriesLeft = POOLING_MAX_RETRIES,
    checkout,
    hasCheckout = false,
  }) => {
    const nextValue = await UserApplication.getUserOverallInfo();
    // Fetch the checkout ONCE per pooling cycle and thread it through the recursion instead of
    // refetching on every retry. A checkout outage must NOT block pooling: degrade a rejection to undefined.
    const resolvedCheckout = hasCheckout
      ? checkout
      : nextValue?.id
        ? await firstAvailableCheckoutByCustomerId({customerId: nextValue.id}).catch(() => undefined)
        : undefined;
    const sanitizingValue = removeNullablePropsFromObject(nextValue);
    const WIPOrder = findWIPOrder(nextValue, resolvedCheckout);
    if (WIPOrder && retriesLeft > 0) {
      await new Promise(resolve => setTimeout(resolve, 1000));
      await getUserOverallInfoPooling({
        dispatch,
        retriesLeft: retriesLeft - 1,
        checkout: resolvedCheckout,
        hasCheckout: true,
      });
    } else {
      if (WIPOrder) void sendLogMessage(`getUserOverallInfoPooling reached max retries for user {${nextValue?.id}}`);
      sanitizingValue.checkout = resolvedCheckout;
      sanitizingValue.nextBoxDate = getNextBoxDate(nextValue.orders, resolvedCheckout);
      sanitizingValue.estimatedCarrierDeliveryAt = getEstimatedCarrierDeliveryAt(nextValue.orders, resolvedCheckout);
      sanitizingValue.isInterimOrder = getCurrentOrderIsInterim(nextValue.orders, resolvedCheckout);
      sanitizingValue.userNextBoxDate = getCurrentOrderUserNextBoxDate(nextValue.orders, resolvedCheckout);
      dispatch({type: ACTIONS.RESET_ACTION_EXECUTED, payload: sanitizingValue});
    }
  };

  const loadUserOverallInfoPooling = getUserOverallInfoPooling.bind(undefined, {dispatch});

  const value = useMemo(
    () => ({
      ...state,
      loadUserOverallInfo,
      keepLoadingUserOverallInfo: () => dispatch({type: ACTIONS.LOAD_USER_OVERALL_INFO_WIP}),
      updateOrder: async order => dispatch({type: ACTIONS.EXECUTE_ACTION_UPDATE_ORDER, payload: order}),
      updateNextBoxDate: date => dispatch({type: ACTIONS.UPDATE_NEXT_BOX_DATE, payload: date}),
      updateEstimatedCarrierDeliveryAt: date =>
        dispatch({type: ACTIONS.UPDATE_ESTIMATED_CARRIER_DELIVERY_AT, payload: date}),
      // TODO not used?
      updateCountry: country => dispatch({type: ACTIONS.UPDATE_COUNTRY, payload: country}),
      executeSkipMonth: () => dispatch({type: ACTIONS.EXECUTE_ACTION_SKIP_MONTH}),
      // TODO not used?
      executeChangeModalityToSubscriber: () => dispatch({type: ACTIONS.EXECUTE_ACTION_CHANGE_MODALITY_TO_SUBSCRIBER}),
      // TODO not used?
      executeChangeNextOrderDate: () => dispatch({type: ACTIONS.EXECUTE_ACTION_CHANGE_NEXT_ORDER_DATE}),
      resetState: () => dispatch({type: ACTIONS.RESET_STATE}),
      // TODO not used?
      updateCardInfo: card => dispatch({type: ACTIONS.UPDATE_CARD, payload: card}),
    }),
    [state, loadUserOverallInfo],
  );

  useEffect(() => {
    const handleUnauthorized = () => setAuthorization(false);
    eventManager.subscribe(UserEvent.UNAUTHORIZED, handleUnauthorized);

    return () => eventManager.unsubscribe(UserEvent.UNAUTHORIZED, handleUnauthorized);
  }, [setAuthorization]);

  useEffect(() => {
    eventManager.subscribe(UserEvent.LOGIN, async () => await value.loadUserOverallInfo());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      if (await AuthApplication.isLoggedIn()) {
        value.loadUserOverallInfo();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (value.WIPOrder) {
      loadUserOverallInfoPooling();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.WIPOrder]);

  useEffect(() => {
    // TODO Albareto In near future we will be able to remove this if which will provide true multilanguage/site but
    // now it is too risky to enable it for every country, so do it only for LU custumers
    if (COUNTRY.BE === site && COUNTRY.LU === value.country) {
      updateSettingsByCountry(value.country);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.country]);

  useEffect(() => {
    if (!value.loading) {
      mendelService.deanonymize(state.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.loading]);

  useEffect(() => {
    value.updateCardInfo({
      cardLastNumbers:
        paymentInstrument.info && paymentInstrument.info.payment_method === PaymentMethods.CARD
          ? paymentInstrument.info.metadata.last4
          : null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentInstrument.info]);

  return <UserOverallInfoContext.Provider value={value}>{children}</UserOverallInfoContext.Provider>;
};

UserOverallInfoProvider.propTypes = {
  children: PropTypes.node,
};

const useOverallInfo = () => useContext(UserOverallInfoContext) || {};

export {
  useOverallInfo,
  UserOverallInfoProvider,
  UserOverallInfoContext,
  getNextBoxDate,
  getEstimatedCarrierDeliveryAt,
  getCurrentOrderIsInterim,
};
